import { useState } from 'react';
import { Icon } from '@/components/ui/Icon';

export type SnoopMode = 'listen' | 'whisper' | 'barge';

/**
 * Live-call supervisor actions, shared by the live-monitor agent cards and the
 * agent-performance rows/cards. All state + handlers live in LiveMonitor; this
 * component is purely presentational so the two surfaces stay in sync.
 */
export interface LiveActions {
  snoop: (channelId: string, mode: SnoopMode, agentId?: number) => Promise<number | null>;
  onTransfer: (agent: { id: number; name: string; call_id?: number | null }) => void;
  onTakeover: (agent: { id: number; name: string; call_id?: number | null }) => void;
}

export interface AgentActionsProps extends LiveActions {
  /** The live agent — must be on-call with a channel_id for actions to show. */
  agent: { id: number; name: string; call_id?: number | null; channel_id?: string | null };
  /** שורת טבלה: כפתורים צרים בשורה אחת (בלי גלישה) כדי שהעמודה תיכנס ב-1440. */
  compact?: boolean;
}

/**
 * Labeled action buttons — icon + text so a supervisor instantly sees what each
 * does (icon-only ghosts were too easy to miss). Snoop actions need the live
 * channel; transfer needs the call id; takeover works with either.
 */
export function AgentActions({ agent, snoop, onTransfer, onTakeover, compact }: AgentActionsProps) {
  const btnStyle = (tone?: 'danger') => baseBtnStyle(tone, compact);
  const ic = compact ? 12 : 13;
  const ch = agent.channel_id;
  // 'more' חושף לחישה/הצטרפות (פעולות שהנציג/הלקוח כן מודעים אליהן).
  // האזנת סתר היא פעולה ישירה — אין עוד פאנל בחירה מאז שהצ'אט לנציג הוסר.
  const [panel, setPanel] = useState<null | 'more'>(null);
  const [busy, setBusy] = useState(false);

  if (!ch && agent.call_id == null) return null;

  // האזנת סתר שקטה — הנציג והלקוח לא מודעים לה.
  async function startListen() {
    if (!ch) return;
    setBusy(true);
    try {
      await snoop(ch, 'listen', agent.id);
      setPanel(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: compact ? 4 : 5, flexWrap: compact ? 'nowrap' : 'wrap', alignItems: 'center', position: 'relative' }}>
      {ch && (
        <>
          {/* האזנת סתר — פעולה ישירה */}
          <button type="button" className="btn primary" style={btnStyle()} disabled={busy}
            onClick={() => void startListen()}
            aria-label="listen"
            title="האזנה שקטה — הנציג והלקוח לא שומעים אותך">
            <Icon name="headphones" size={ic} /> האזנה
          </button>

          {/* פעולות משניות (הנציג/הלקוח מודעים) */}
          <button type="button" className="btn" style={btnStyle()}
            onClick={() => setPanel((p) => (p === 'more' ? null : 'more'))}
            aria-label="more" title="עוד פעולות">
            <Icon name="more" size={ic} /> עוד
          </button>
          {panel === 'more' && (
            <ChoicePanel onClose={() => setPanel(null)}>
              <ChoiceButton icon="mic" tone="default" disabled={busy}
                title="לחישה" desc="רק הנציג שומע אותך"
                onClick={() => { void snoop(ch, 'whisper', agent.id); setPanel(null); }} />
              <ChoiceButton icon="bolt" tone="default" disabled={busy}
                title="הצטרפות לשיחה" desc="כולם שומעים אותך"
                onClick={() => { void snoop(ch, 'barge', agent.id); setPanel(null); }} />
            </ChoicePanel>
          )}
        </>
      )}
      {agent.call_id != null && (
        <button type="button" className="btn" style={btnStyle()} onClick={() => onTransfer(agent)}
          aria-label="transfer" title="העברת השיחה לנציג אחר">
          <Icon name="send" size={ic} /> העברה
        </button>
      )}
      <button type="button" className="btn" style={btnStyle('danger')} onClick={() => onTakeover(agent)}
        aria-label="takeover" title="חטיפת השיחה אליך — הנציג ינותק">
        <Icon name="moveRight" size={ic} /> חטיפה
      </button>
    </div>
  );
}

/** פאנל צף קטן מתחת לכפתור, עם סגירה בלחיצה בחוץ. */
function ChoicePanel({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} aria-hidden />
      <div
        role="menu"
        style={{
          position: 'absolute', top: 'calc(100% + 6px)', insetInlineStart: 0, zIndex: 41,
          minWidth: 240, display: 'flex', flexDirection: 'column', gap: 4,
          padding: 6, background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-2, 0 8px 24px rgba(0,0,0,.18))',
        }}
      >
        {children}
      </div>
    </>
  );
}

function ChoiceButton({ icon, title, desc, tone, disabled, onClick }: {
  icon: string; title: string; desc: string; tone: 'default' | 'accent'; disabled?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button" role="menuitem" disabled={disabled} onClick={onClick}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'start',
        padding: '10px 12px', borderRadius: 'var(--r-2)', border: '1px solid var(--line)',
        background: tone === 'accent' ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface-2)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, width: '100%',
      }}
    >
      <Icon name={icon} size={18} color={tone === 'accent' ? 'var(--accent)' : 'var(--fg)'} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{title}</strong>
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{desc}</span>
      </span>
    </button>
  );
}

function baseBtnStyle(tone?: 'danger', compact?: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: compact ? 4 : 5,
    fontSize: 12,
    fontWeight: 600,
    height: compact ? 30 : 32,
    padding: compact ? '0 8px' : '0 10px',
    borderRadius: 7,
    whiteSpace: 'nowrap',
    ...(tone === 'danger'
      ? { color: 'var(--danger)', borderColor: 'var(--danger)' }
      : null),
  };
}
